import React, { useState, useMemo, useCallback, useEffect, useRef, memo, startTransition } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useSearchParams, Link, useNavigate, useParams } from "react-router-dom";
import {
  X,
  SlidersHorizontal,
  Package,
  Search,
  Loader2,
} from "lucide-react";
import { categoryService, productService } from "../services/firebaseConfig";
import { Product, Category } from "../types";
import ProductCard from "../components/ProductCard";
import {
  useCompleteCategoryHierarchy,
  useTopLevelCategories,
  useCategoryById,
  useCategoryBreadcrumb,
} from "../hooks/useCategories";

// ============== Components ==============

// Price Range Slider Component (matching StoresDirectoryPage)
const PriceRangeSlider: React.FC<{
  min: number;
  max: number;
  value: [number, number];
  onChange: (value: [number, number]) => void;
}> = ({ min, max, value, onChange }) => {
  const handleMinChange = (newMin: number) => {
    const clampedMin = Math.max(min, Math.min(newMin, value[1] - 1));
    onChange([clampedMin, value[1]]);
  };

  const handleMaxChange = (newMax: number) => {
    const clampedMax = Math.min(max, Math.max(newMax, value[0] + 1));
    onChange([value[0], clampedMax]);
  };

  const range = max - min;
  const minPercent = range > 0 ? ((value[0] - min) / range) * 100 : 0;
  const maxPercent = range > 0 ? ((value[1] - min) / range) * 100 : 100;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between text-sm font-medium text-gray-900 font-poppins">
        <span>Rs. {value[0].toLocaleString()}</span>
        <span className="text-gray-400">-</span>
        <span>Rs. {value[1].toLocaleString()}</span>
      </div>
      <div className="relative h-2 bg-gray-200 rounded-full">
        <div
          className="absolute h-full bg-primary-500 rounded-full"
          style={{
            left: `${minPercent}%`,
            width: `${Math.max(0, maxPercent - minPercent)}%`,
          }}
        />
        <input
          type="range"
          min={min}
          max={max}
          value={value[0]}
          onChange={(e) => handleMinChange(Number(e.target.value))}
          className="absolute w-full h-2 opacity-0 cursor-pointer z-10"
        />
        <input
          type="range"
          min={min}
          max={max}
          value={value[1]}
          onChange={(e) => handleMaxChange(Number(e.target.value))}
          className="absolute w-full h-2 opacity-0 cursor-pointer z-10"
        />
        <div
          className="absolute top-1/2 -translate-y-1/2 w-4 h-4 bg-white border-2 border-primary-500 rounded-full shadow pointer-events-none"
          style={{ left: `calc(${minPercent}% - 8px)` }}
        />
        <div
          className="absolute top-1/2 -translate-y-1/2 w-4 h-4 bg-white border-2 border-primary-500 rounded-full shadow pointer-events-none"
          style={{ left: `calc(${maxPercent}% - 8px)` }}
        />
      </div>
      <div className="flex gap-3">
        <input
          type="number"
          min={min}
          max={value[1] - 1}
          value={value[0]}
          onChange={(e) => handleMinChange(Number(e.target.value))}
          className="flex-1 px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 font-poppins"
          placeholder="Min"
        />
        <input
          type="number"
          min={value[0] + 1}
          max={max}
          value={value[1]}
          onChange={(e) => handleMaxChange(Number(e.target.value))}
          className="flex-1 px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 font-poppins"
          placeholder="Max"
        />
      </div>
    </div>
  );
};

// Active Filter Pill (matching StoresDirectoryPage)
const FilterPill: React.FC<{
  label: string;
  onRemove: () => void;
}> = ({ label, onRemove }) => (
  <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-primary-100 text-primary-700 text-sm font-medium rounded-full font-poppins">
    {label}
    <button
      onClick={onRemove}
      className="p-0.5 hover:bg-primary-200 rounded-full transition-colors"
    >
      <X className="w-3.5 h-3.5" />
    </button>
  </span>
);

// Search Suggestions Component
interface SearchSuggestion {
  type: "product" | "category";
  id: string;
  name: string;
  category?: string;
  price?: number;
  imageUrl?: string;
}

const SearchSuggestions: React.FC<{
  suggestions: SearchSuggestion[];
  selectedIndex: number;
  onSelect: (suggestion: SearchSuggestion) => void;
  searchQuery: string;
}> = ({ suggestions, selectedIndex, onSelect, searchQuery }) => {
  const highlightMatch = (text: string, query: string) => {
    if (!query) return text;
    const parts = text.split(new RegExp(`(${query})`, "gi"));
    return parts.map((part, i) =>
      part.toLowerCase() === query.toLowerCase() ? (
        <strong key={i} className="text-primary-600 font-semibold">
          {part}
        </strong>
      ) : (
        part
      )
    );
  };

  return (
    <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-xl shadow-xl z-50 max-h-96 overflow-y-auto">
      {suggestions.map((suggestion, index) => (
        <button
          key={`${suggestion.type}-${suggestion.id}`}
          onClick={() => onSelect(suggestion)}
          className={`w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-gray-50 transition-colors font-poppins border-b border-gray-100 last:border-b-0 ${
            index === selectedIndex ? "bg-primary-50" : ""
          }`}
        >
          {suggestion.imageUrl ? (
            <img
              src={suggestion.imageUrl}
              alt=""
              className="w-10 h-10 rounded-lg object-cover bg-gray-100"
            />
          ) : (
            <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center">
              <Search className="w-4 h-4 text-gray-400" />
            </div>
          )}
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-gray-900 truncate">
              {highlightMatch(suggestion.name, searchQuery)}
            </p>
            {suggestion.type === "product" && suggestion.category && (
              <p className="text-xs text-gray-500 truncate">
                in {suggestion.category}
              </p>
            )}
            {suggestion.type === "category" && (
              <p className="text-xs text-primary-600 font-medium">Category</p>
            )}
          </div>
          {suggestion.type === "product" && suggestion.price && (
            <span className="text-sm font-semibold text-primary-600">
              Rs. {suggestion.price.toLocaleString()}
            </span>
          )}
        </button>
      ))}
    </div>
  );
};

// Memoized Category Button Component for better performance
const CategoryButton = memo<{
  category: Category;
  isActive: boolean;
  onClick: () => void;
}>(({ category, isActive, onClick }) => {
  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    onClick();
  };

  return (
    <button
      onClick={handleClick}
      type="button"
      className={`w-full text-left px-4 py-2.5 text-sm rounded-lg border-2 transition-all font-poppins ${
        isActive
          ? "bg-primary-600 text-white border-primary-600 font-medium"
          : "bg-white text-gray-700 border-gray-200 hover:border-primary-300 hover:bg-primary-50"
      }`}
    >
      {category.name}
    </button>
  );
});

CategoryButton.displayName = "CategoryButton";

// Memoized Subcategory Button Component for better performance
const SubcategoryButton = memo<{
  subcategory: Category;
  isActive: boolean;
  onClick: () => void;
}>(({ subcategory, isActive, onClick }) => {
  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    onClick();
  };

  return (
    <button
      onClick={handleClick}
      type="button"
      className={`w-full text-left px-3 py-2 text-sm rounded-lg border transition-all font-poppins ${
        isActive
          ? "bg-primary-600 text-white border-primary-600 font-medium"
          : "bg-white text-gray-700 border-gray-200 hover:border-primary-300 hover:bg-primary-50"
      }`}
    >
      {subcategory.name}
    </button>
  );
});

SubcategoryButton.displayName = "SubcategoryButton";

// ============== Main Component ==============

const CategoriesPageNew: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { categorySlug } = useParams<{ categorySlug?: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const categoryParam = searchParams.get("category") || categorySlug;

  // State
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedSuggestionIndex, setSelectedSuggestionIndex] = useState(-1);
  const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>(null);
  const [selectedParentCategoryId, setSelectedParentCategoryId] = useState<string | null>(null); // Track parent for subcategory display
  const [selectedPrimaryCategoryName, setSelectedPrimaryCategoryName] = useState<string | null>(null);
  const [selectedSubcategoryName, setSelectedSubcategoryName] = useState<string | null>(null); // Track subcategory name for filtering
  const [priceRange, setPriceRange] = useState<[number, number]>([0, 50000]);
  const [selectedSizes, setSelectedSizes] = useState<string[]>([]);
  const [selectedColors, setSelectedColors] = useState<string[]>([]);
  
  const suggestionsRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const isInternalUpdateRef = useRef(false);

  // Fetch category hierarchy with caching
  const { data: categoryData, isLoading: categoriesLoading } = useCompleteCategoryHierarchy();
  const { data: mainCategoriesData } = useTopLevelCategories();

  // Get main categories
  const categories = useMemo(() => {
    return categoryData?.topLevelCategories || mainCategoriesData || [];
  }, [categoryData, mainCategoriesData]);

  // Get hierarchy map
  const hierarchy = useMemo(() => {
    if (!categoryData?.hierarchy) return new Map<string, Category[]>();
    if (categoryData.hierarchy instanceof Map) return categoryData.hierarchy;
    const map = new Map<string, Category[]>();
    Object.entries(categoryData.hierarchy).forEach(([key, value]) => {
      map.set(key, value as Category[]);
    });
    return map;
  }, [categoryData?.hierarchy]);

  // Update selectedCategoryId when categoryParam changes (from URL)
  useEffect(() => {
    // Skip if this is an internal update from our click handlers
    if (isInternalUpdateRef.current) {
      isInternalUpdateRef.current = false;
      return;
    }

    if (categoryParam && categories.length > 0) {
      // Validate that the category exists in our loaded categories
      const categoryExists = categories.some((cat: Category) => cat.id === categoryParam) ||
                           hierarchy.has(categoryParam) ||
                           Array.from(hierarchy.values()).some((children: unknown) => 
                             Array.isArray(children) && children.some((child: unknown) => 
                               typeof child === 'object' && child && 'id' in child && (child as Category).id === categoryParam
                             )
                           );
      
      if (categoryExists) {
        if (categoryParam !== selectedCategoryId) {
          setSelectedCategoryId(categoryParam);
          setSelectedParentCategoryId(null);
          setSelectedPrimaryCategoryName(null);
          setSelectedSubcategoryName(null);
        }
      } else {
        // Category doesn't exist, clear selection and show all products
        console.warn(`Category "${categoryParam}" not found, showing all products`);
        setSelectedCategoryId(null);
        setSelectedParentCategoryId(null);
        setSelectedPrimaryCategoryName(null);
        setSelectedSubcategoryName(null);
        // Remove invalid category from URL
        setSearchParams({});
      }
    } else if (!categoryParam && selectedCategoryId) {
      // Clear selection if no category param
      setSelectedCategoryId(null);
      setSelectedParentCategoryId(null);
      setSelectedPrimaryCategoryName(null);
      setSelectedSubcategoryName(null);
    }
  }, [categoryParam, categories, hierarchy, setSearchParams]);

  // Create category map for breadcrumb display
  const categoryMap = useMemo(() => {
    const map: Record<string, string> = {};
    const buildPath = (categoryId: string, path: string[] = []): void => {
      const children = hierarchy.get(categoryId);
      if (children) {
        children.forEach((child: Category) => {
          const parentCat = categories.find((c: Category) => c.id === categoryId);
          const parentName = parentCat?.name || "";
          map[child.id] = parentName;
          buildPath(child.id, [...path, child.name]);
        });
      }
    };
    categories.forEach((cat: Category) => buildPath(cat.id, [cat.name]));
    return map;
  }, [categories, hierarchy]);

  // Get current category info
  const { data: currentCategory } = useCategoryById(selectedCategoryId || "");
  const { data: breadcrumbData } = useCategoryBreadcrumb(selectedCategoryId || "");

  // Fetch child categories for selected parent (use parentCategoryId to keep subcategories visible)
  const parentIdForChildren = selectedParentCategoryId || selectedCategoryId;
  const { data: childCategories } = useQuery({
    queryKey: ["childCategories", parentIdForChildren],
    queryFn: async () => {
      if (!parentIdForChildren) return [];
      // First check hierarchy cache
      const cached = hierarchy.get(parentIdForChildren);
      if (cached) return cached;
      // Otherwise fetch
      return categoryService.getSubcategories(parentIdForChildren);
    },
    enabled: !!parentIdForChildren,
    staleTime: 5 * 60 * 1000,
  });

  // ============== PARALLEL PRODUCT FETCHING ==============
  // Fetch all descendant category IDs for comprehensive product filtering
  const { data: allCategoryIds, isLoading: idsLoading } = useQuery({
    queryKey: ["categoryDescendants", selectedCategoryId],
    queryFn: async () => {
      if (!selectedCategoryId) return [];
      
      const ids: string[] = [selectedCategoryId];
      const toProcess = [selectedCategoryId];

      while (toProcess.length > 0) {
        const currentId = toProcess.shift()!;
        let children = hierarchy.get(currentId);
        
        if (!children) {
          try {
            children = await categoryService.getSubcategories(currentId);
          } catch {
            children = [];
          }
        }

        children?.forEach((child: Category) => {
          ids.push(child.id);
          toProcess.push(child.id);
        });
      }

      return ids;
    },
    enabled: !!selectedCategoryId,
    staleTime: 10 * 60 * 1000, // Cache for 10 minutes
  });

  // Fetch ALL products with parallel query (starts immediately)
  const { data: allProducts = [], isLoading: productsLoading } = useQuery({
    queryKey: ["allProducts"],
    queryFn: async () => {
      // Use getAllProducts to fetch all products regardless of status (same as StoresDirectoryPage)
      const products = await productService.getAllProducts();
      return products || [];
    },
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });

  // Pre-fetch products for main categories (parallel prefetching for instant switching)
  useEffect(() => {
    if (categories.length > 0 && allProducts.length > 0) {
      // Pre-compute category products for quick access
      categories.forEach((cat: Category) => {
        const descendants = [cat.id];
        const children = hierarchy.get(cat.id);
        if (children) {
          children.forEach((child: Category) => {
            descendants.push(child.id);
            const grandchildren = hierarchy.get(child.id);
            if (grandchildren) {
              grandchildren.forEach((gc: Category) => descendants.push(gc.id));
            }
          });
        }
        // Pre-cache in query client
        queryClient.setQueryData(["categoryDescendants", cat.id], descendants);
      });
    }
  }, [categories, hierarchy, allProducts, queryClient]);

  // Filter products based on selection (instant filtering - no server call) - optimized
  const filteredProducts = useMemo(() => {
    const products = allProducts || [];
    if (products.length === 0) return [];

    let filtered = products;

    // Filter by selected category name (primary or subcategory)
    const filterCategoryName = selectedSubcategoryName || selectedPrimaryCategoryName;
    if (filterCategoryName) {
      const categoryLower = filterCategoryName.toLowerCase();
      filtered = filtered.filter((p) => {
        // Method 1: Check categoryIds array (fastest path)
        if (p.categoryIds && Array.isArray(p.categoryIds) && p.categoryIds.length > 0) {
          const hasMatch = p.categoryIds.some((catId: string) => {
            if (!catId) return false;
            const catIdLower = String(catId).toLowerCase();
            if (catIdLower === categoryLower) return true;
            if (catIdLower.startsWith(categoryLower + '_')) return true;
            if (catIdLower.endsWith('_' + categoryLower)) return true;
            const segments = catIdLower.split('_');
            if (segments.includes(categoryLower)) return true;
            return false;
          });
          if (hasMatch) return true;
        }
        // Method 2: Check categories array (names)
        if (p.categories && Array.isArray(p.categories) && p.categories.length > 0) {
          const hasMatch = p.categories.some((catName: string) => {
            if (!catName) return false;
            const nameLower = String(catName).toLowerCase();
            if (nameLower === categoryLower) return true;
            if (nameLower.includes(categoryLower)) return true;
            return false;
          });
          if (hasMatch) return true;
        }
        return false;
      });
    } else if (allCategoryIds && allCategoryIds.length > 0) {
      // Filter by specific category ID (subcategory selected)
      filtered = filtered.filter((p) =>
        (p.categoryIds && Array.isArray(p.categoryIds) && p.categoryIds.some((catId) => allCategoryIds.includes(catId))) ||
        (p.categories && Array.isArray(p.categories) && p.categories.some((cat) => allCategoryIds.includes(cat)))
      );
    }

    // Filter by search
    const searchQuery = searchInput.trim();
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter((p) => {
        const title = p.title?.toLowerCase() || '';
        const description = p.description?.toLowerCase() || '';
        const brand = p.brand?.toLowerCase() || '';
        return title.includes(query) || description.includes(query) || brand.includes(query);
      });
    }

    // Filter by price range
    filtered = filtered.filter((p) => {
      const price = p.discountPrice || p.price || 0;
      return price >= priceRange[0] && price <= priceRange[1];
    });

    // Filter by selected sizes
    if (selectedSizes.length > 0) {
      filtered = filtered.filter((p) =>
        p.sizes && Array.isArray(p.sizes) && p.sizes.some((size) => selectedSizes.includes(size))
      );
    }

    // Filter by selected colors
    if (selectedColors.length > 0) {
      filtered = filtered.filter((p) =>
        p.colors && Array.isArray(p.colors) && p.colors.some((color) => selectedColors.includes(color))
      );
    }

    return filtered;
  }, [allProducts, selectedPrimaryCategoryName, selectedSubcategoryName, allCategoryIds, searchInput, priceRange, selectedSizes, selectedColors]);

  // Calculate price stats for slider
  const priceStats = useMemo(() => {
    if (allProducts.length === 0) return { min: 0, max: 50000 };
    const prices = allProducts.map((p) => p.discountPrice || p.price || 0).filter((p) => p > 0);
    return {
      min: Math.floor(Math.min(...prices, 0)),
      max: Math.ceil(Math.max(...prices, 50000)),
    };
  }, [allProducts]);

  // Extract available sizes and colors from all products
  const availableSizes = useMemo(() => {
    const sizes = new Set<string>();
    allProducts.forEach((p) => {
      p.sizes?.forEach((size) => sizes.add(size));
    });
    return Array.from(sizes).sort();
  }, [allProducts]);

  const availableColors = useMemo(() => {
    const colors = new Set<string>();
    allProducts.forEach((p) => {
      p.colors?.forEach((color) => colors.add(color));
    });
    return Array.from(colors).sort();
  }, [allProducts]);

  // Search suggestions
  const searchSuggestions = useMemo<SearchSuggestion[]>(() => {
    if (!searchInput.trim() || searchInput.length < 2) return [];
    
    const query = searchInput.toLowerCase();
    const suggestions: SearchSuggestion[] = [];

    // Category suggestions
    categories.forEach((cat: Category) => {
      if (cat.name.toLowerCase().includes(query)) {
        suggestions.push({
          type: "category",
          id: cat.id,
          name: cat.name,
          imageUrl: cat.thumbnailUrl || cat.imageUrl,
        });
      }
    });

    // Product suggestions (limit to 5)
    allProducts
      .filter((p) => p.title?.toLowerCase().includes(query))
      .slice(0, 5)
      .forEach((p) => {
        suggestions.push({
          type: "product",
          id: p.id,
          name: p.title,
          category: p.categories?.[0],
          price: p.discountPrice || p.price,
          imageUrl: p.images?.[0] || p.thumbnail,
        });
      });

    return suggestions.slice(0, 8);
  }, [searchInput, categories, allProducts]);

  // Handle suggestion selection
  const handleSuggestionSelect = useCallback(
    (suggestion: SearchSuggestion) => {
      if (suggestion.type === "category") {
        const cat = categories.find((c: Category) => c.id === suggestion.id);
        if (cat) {
          setSelectedCategoryId(cat.id);
          setSelectedPrimaryCategoryName(cat.name);
        }
      } else {
        navigate(`/product/${suggestion.id}`);
      }
      setSearchInput("");
      setShowSuggestions(false);
    },
    [categories, navigate]
  );

  // Keyboard navigation for suggestions
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (!showSuggestions || searchSuggestions.length === 0) return;

      switch (e.key) {
        case "ArrowDown":
          e.preventDefault();
          setSelectedSuggestionIndex((prev) =>
            prev < searchSuggestions.length - 1 ? prev + 1 : 0
          );
          break;
        case "ArrowUp":
          e.preventDefault();
          setSelectedSuggestionIndex((prev) =>
            prev > 0 ? prev - 1 : searchSuggestions.length - 1
          );
          break;
        case "Enter":
          e.preventDefault();
          if (selectedSuggestionIndex >= 0) {
            handleSuggestionSelect(searchSuggestions[selectedSuggestionIndex]);
          }
          break;
        case "Escape":
          setShowSuggestions(false);
          setSelectedSuggestionIndex(-1);
          break;
      }
    },
    [showSuggestions, searchSuggestions, selectedSuggestionIndex, handleSuggestionSelect]
  );

  // Click outside to close suggestions
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (suggestionsRef.current && !suggestionsRef.current.contains(e.target as Node)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Quick filter presets
  const handleQuickPreset = useCallback((preset: string) => {
    switch (preset) {
      case "under_1000":
        setPriceRange([0, 1000]);
        break;
      case "1000_5000":
        setPriceRange([1000, 5000]);
        break;
      case "5000_10000":
        setPriceRange([5000, 10000]);
        break;
      case "above_10000":
        setPriceRange([10000, priceStats.max]);
        break;
    }
  }, [priceStats.max]);

  // Clear all filters
  const handleClearAllFilters = useCallback(() => {
    setSelectedCategoryId(null);
    setSelectedParentCategoryId(null);
    setSelectedPrimaryCategoryName(null);
    setSelectedSubcategoryName(null);
    setPriceRange([0, 50000]);
    setSearchInput("");
    setSelectedSizes([]);
    setSelectedColors([]);
    setSearchParams({});
  }, [setSearchParams]);

  // Optimized category click handler (no toggle - keeps active when clicked again)
  const handleCategoryClick = useCallback((category: Category) => {
    // Mark as internal update to prevent useEffect sync
    isInternalUpdateRef.current = true;

    // Immediate state updates (batched)
    startTransition(() => {
      setSelectedCategoryId(category.id);
      setSelectedParentCategoryId(category.id);
      setSelectedPrimaryCategoryName(category.name);
      setSelectedSubcategoryName(null); // Clear subcategory when selecting parent
    });
    
    // Defer URL update
    startTransition(() => {
      setSearchParams({ category: category.id }, { replace: true });
    });
  }, [setSearchParams]);

  // Optimized subcategory click handler
  const handleSubcategoryClick = useCallback((subcategory: Category, parentId: string) => {
    // Mark as internal update to prevent useEffect sync
    isInternalUpdateRef.current = true;

    // Immediate state updates (batched)
    startTransition(() => {
      setSelectedCategoryId(subcategory.id);
      setSelectedSubcategoryName(subcategory.name);
      // Keep selectedParentCategoryId the same so subcategories stay visible
      setSelectedPrimaryCategoryName(null);
    });
    
    // Defer URL update
    startTransition(() => {
      setSearchParams({ category: subcategory.id }, { replace: true });
    });
  }, [setSearchParams]);

  // Optimized "All Categories" click handler
  const handleAllCategoriesClick = useCallback(() => {
    // Mark as internal update to prevent useEffect sync
    isInternalUpdateRef.current = true;

    // Immediate state updates (batched)
    startTransition(() => {
      setSelectedCategoryId(null);
      setSelectedParentCategoryId(null);
      setSelectedPrimaryCategoryName(null);
      setSelectedSubcategoryName(null);
    });
    
    // Defer URL update
    startTransition(() => {
      setSearchParams({}, { replace: true });
    });
  }, [setSearchParams]);

  // Toggle size filter
  const handleSizeToggle = useCallback((size: string) => {
    setSelectedSizes((prev) =>
      prev.includes(size) ? prev.filter((s) => s !== size) : [...prev, size]
    );
  }, []);

  // Toggle color filter
  const handleColorToggle = useCallback((color: string) => {
    setSelectedColors((prev) =>
      prev.includes(color) ? prev.filter((c) => c !== color) : [...prev, color]
    );
  }, []);

  // Get active filters
  const activeFilters = useMemo(() => {
    const filters: { label: string; onRemove: () => void }[] = [];

    if (priceRange[0] > 0 || priceRange[1] < 50000) {
      filters.push({
        label: `Rs. ${priceRange[0].toLocaleString()} - Rs. ${priceRange[1].toLocaleString()}`,
        onRemove: () => setPriceRange([0, 50000]),
      });
    }

    if (searchInput) {
      filters.push({
        label: `"${searchInput}"`,
        onRemove: () => setSearchInput(""),
      });
    }

    selectedSizes.forEach((size) => {
      filters.push({
        label: `Size: ${size}`,
        onRemove: () => setSelectedSizes((prev) => prev.filter((s) => s !== size)),
      });
    });

    selectedColors.forEach((color) => {
      filters.push({
        label: `Color: ${color}`,
        onRemove: () => setSelectedColors((prev) => prev.filter((c) => c !== color)),
      });
    });

    return filters;
  }, [priceRange, searchInput, selectedSizes, selectedColors]);

  const activeFilterCount = activeFilters.length + (selectedCategoryId ? 1 : 0);

  // Page title
  const pageTitle = selectedPrimaryCategoryName || currentCategory?.name || "All Products";

  return (
    <div className="min-h-screen bg-gray-50 pb-20 md:pb-0 pt-4">
      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="flex gap-6">
          {/* Desktop Sidebar */}
          <aside className="hidden lg:block w-72 flex-shrink-0">
            <div className="sticky top-20 bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2 font-poppins">
                <SlidersHorizontal className="w-5 h-5 text-primary-600" />
                <span>Filters</span>
                {activeFilterCount > 0 && (
                  <span className="px-2 py-0.5 bg-primary-600 text-white text-xs font-semibold rounded-full">
                    {activeFilterCount}
                  </span>
                )}
              </h2>

              <div className="space-y-6">
                {/* Quick Filter Presets */}
                <div>
                  <h3 className="text-sm font-semibold text-gray-900 mb-3 uppercase tracking-wide font-poppins">
                    Quick Filters
                  </h3>
                  <div className="space-y-2">
                    <button
                      onClick={() => handleQuickPreset("under_1000")}
                      className="w-full text-left px-3 py-2 text-sm rounded-lg border border-gray-200 hover:border-primary-300 hover:bg-primary-50 transition-colors font-poppins"
                    >
                      Under Rs. 1,000
                    </button>
                    <button
                      onClick={() => handleQuickPreset("1000_5000")}
                      className="w-full text-left px-3 py-2 text-sm rounded-lg border border-gray-200 hover:border-primary-300 hover:bg-primary-50 transition-colors font-poppins"
                    >
                      Rs. 1,000 - Rs. 5,000
                    </button>
                    <button
                      onClick={() => handleQuickPreset("5000_10000")}
                      className="w-full text-left px-3 py-2 text-sm rounded-lg border border-gray-200 hover:border-primary-300 hover:bg-primary-50 transition-colors font-poppins"
                    >
                      Rs. 5,000 - Rs. 10,000
                    </button>
                    <button
                      onClick={() => handleQuickPreset("above_10000")}
                      className="w-full text-left px-3 py-2 text-sm rounded-lg border border-gray-200 hover:border-primary-300 hover:bg-primary-50 transition-colors font-poppins"
                    >
                      Above Rs. 10,000
                    </button>
                  </div>
                </div>

                {/* Price Filter */}
                <div>
                  <h3 className="text-sm font-semibold text-gray-900 mb-4 uppercase tracking-wide font-poppins">
                    Price Range
                  </h3>
                  <PriceRangeSlider
                    min={priceStats.min}
                    max={priceStats.max}
                    value={priceRange}
                    onChange={setPriceRange}
                  />
                </div>

                {/* Category Filter */}
                <div>
                  <h3 className="text-sm font-semibold text-gray-900 mb-3 uppercase tracking-wide font-poppins">
                    By Categories
                  </h3>
                  <div className="space-y-2">
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleAllCategoriesClick();
                      }}
                      type="button"
                      className={`w-full text-left px-4 py-2.5 text-sm rounded-lg border-2 transition-all font-poppins ${
                        !selectedCategoryId && !selectedPrimaryCategoryName
                          ? "bg-primary-600 text-white border-primary-600 font-medium"
                          : "bg-white text-gray-700 border-gray-200 hover:border-primary-300 hover:bg-primary-50"
                      }`}
                    >
                      All Categories
                    </button>

                    {/* Primary Category Buttons */}
                    {categoriesLoading ? (
                      <div className="space-y-2">
                        {[...Array(5)].map((_, i) => (
                          <div key={i} className="h-10 bg-gray-100 rounded-lg animate-pulse" />
                        ))}
                      </div>
                    ) : (
                      categories.map((category: Category) => {
                        const isActive = selectedParentCategoryId === category.id || selectedPrimaryCategoryName === category.name;
                        return (
                          <div key={category.id}>
                            <CategoryButton
                              category={category}
                              isActive={isActive}
                              onClick={() => handleCategoryClick(category)}
                            />
                            {/* Subcategories - shown directly below selected category */}
                            {childCategories && childCategories.length > 0 && selectedParentCategoryId === category.id && (
                              <div className="mt-1 ml-3 space-y-1 border-l-2 border-primary-200 pl-2">
                                {childCategories.map((child: Category) => {
                                  const isSubActive = selectedCategoryId === child.id;
                                  return (
                                    <SubcategoryButton
                                      key={child.id}
                                      subcategory={child}
                                      isActive={isSubActive}
                                      onClick={() => handleSubcategoryClick(child, category.id)}
                                    />
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        );
                      })
                    )}
                  </div>
                </div>

                {/* Sizes Filter */}
                {availableSizes.length > 0 && (
                  <div>
                    <h3 className="text-sm font-semibold text-gray-900 mb-3 uppercase tracking-wide font-poppins">
                      Sizes
                    </h3>
                    <div className="flex flex-wrap gap-2">
                      {availableSizes.map((size) => (
                        <button
                          key={size}
                          onClick={() => handleSizeToggle(size)}
                          className={`px-3 py-1.5 text-sm rounded-lg border transition-all font-poppins ${
                            selectedSizes.includes(size)
                              ? "bg-primary-600 text-white border-primary-600 font-medium"
                              : "bg-white text-gray-700 border-gray-200 hover:border-primary-300 hover:bg-primary-50"
                          }`}
                        >
                          {size}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Colors Filter */}
                {availableColors.length > 0 && (
                  <div>
                    <h3 className="text-sm font-semibold text-gray-900 mb-3 uppercase tracking-wide font-poppins">
                      Colors
                    </h3>
                    <div className="flex flex-wrap gap-2">
                      {availableColors.map((color) => (
                        <button
                          key={color}
                          onClick={() => handleColorToggle(color)}
                          className={`px-3 py-1.5 text-sm rounded-lg border transition-all font-poppins ${
                            selectedColors.includes(color)
                              ? "bg-primary-600 text-white border-primary-600 font-medium"
                              : "bg-white text-gray-700 border-gray-200 hover:border-primary-300 hover:bg-primary-50"
                          }`}
                        >
                          {color}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Clear All Filters Button */}
                {activeFilterCount > 0 && (
                  <div className="pt-4 border-t border-gray-200">
                    <button
                      onClick={handleClearAllFilters}
                      className="w-full py-2.5 px-4 bg-primary-600 text-white text-sm font-medium rounded-lg hover:bg-primary-700 transition-colors font-poppins"
                    >
                      Clear All Filters
                    </button>
                  </div>
                )}
              </div>
            </div>
          </aside>

          {/* Main Content */}
          <main className="flex-1 min-w-0">
            {/* Mobile Filter Button */}
            <div className="lg:hidden mb-4">
              <button
                onClick={() => setMobileFiltersOpen(true)}
                className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors font-poppins w-full justify-center"
              >
                <SlidersHorizontal className="w-4 h-4" />
                Filters
                {activeFilterCount > 0 && (
                  <span className="ml-1 px-2 py-0.5 bg-primary-600 text-white text-xs font-semibold rounded-full">
                    {activeFilterCount}
                  </span>
                )}
              </button>
            </div>

            {/* Search Bar */}
            <div className="mb-6">
              <div className="relative" ref={suggestionsRef}>
                <input
                  ref={searchInputRef}
                  type="text"
                  placeholder="Search products, categories, brands..."
                  value={searchInput}
                  onChange={(e) => {
                    setSearchInput(e.target.value);
                    setShowSuggestions(true);
                    setSelectedSuggestionIndex(-1);
                  }}
                  onFocus={() => {
                    if (searchSuggestions.length > 0) {
                      setShowSuggestions(true);
                    }
                  }}
                  onKeyDown={handleKeyDown}
                  className="w-full pl-11 pr-4 py-3 rounded-lg border border-gray-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 font-poppins"
                />
                <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                {showSuggestions && searchSuggestions.length > 0 && (
                  <SearchSuggestions
                    suggestions={searchSuggestions}
                    selectedIndex={selectedSuggestionIndex}
                    onSelect={handleSuggestionSelect}
                    searchQuery={searchInput}
                  />
                )}
              </div>
            </div>

            {/* Results Header - Active Filters */}
            {(activeFilters.length > 0 || selectedCategoryId) && (
              <div className="bg-white rounded-xl border border-gray-200 p-4 mb-6">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-sm text-gray-600 font-poppins">Active Filters:</span>
                  {selectedPrimaryCategoryName && !selectedSubcategoryName && (
                    <FilterPill
                      label={selectedPrimaryCategoryName}
                      onRemove={() => {
                        setSelectedCategoryId(null);
                        setSelectedParentCategoryId(null);
                        setSelectedPrimaryCategoryName(null);
                        setSelectedSubcategoryName(null);
                        setSearchParams({});
                      }}
                    />
                  )}
                  {selectedSubcategoryName && (
                    <FilterPill
                      label={`${selectedPrimaryCategoryName ? selectedPrimaryCategoryName + ' > ' : ''}${selectedSubcategoryName}`}
                      onRemove={() => {
                        // Go back to parent category
                        if (selectedParentCategoryId) {
                          setSelectedCategoryId(selectedParentCategoryId);
                          const parent = categories.find((c: Category) => c.id === selectedParentCategoryId);
                          setSelectedPrimaryCategoryName(parent?.name || null);
                        } else {
                          setSelectedCategoryId(null);
                          setSelectedPrimaryCategoryName(null);
                        }
                        setSelectedSubcategoryName(null);
                        setSearchParams({ category: selectedParentCategoryId || '' });
                      }}
                    />
                  )}
                  {activeFilters.map((filter, index) => (
                    <FilterPill key={index} label={filter.label} onRemove={filter.onRemove} />
                  ))}
                  <button
                    onClick={handleClearAllFilters}
                    className="text-sm text-primary-600 hover:text-primary-700 font-medium ml-2 font-poppins"
                  >
                    Clear All
                  </button>
                </div>
              </div>
            )}

            {/* Products Grid */}
            {productsLoading ? (
              <div className="flex justify-center items-center py-14">
                <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
              </div>
            ) : filteredProducts.length === 0 ? (
              <>
                {/* No results message */}
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6">
                  <p className="text-sm text-amber-800 font-poppins">
                    <span className="font-semibold">No products match your filters.</span> Showing all available products below.
                  </p>
                </div>
                {/* Show all products when filter returns empty */}
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-4 2xl:grid-cols-5 gap-2 sm:gap-3 md:gap-4">
                  {allProducts.map((product) => (
                    <ProductCard key={product.id} product={product} compact />
                  ))}
                </div>
              </>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-4 2xl:grid-cols-5 gap-2 sm:gap-3 md:gap-4">
                {filteredProducts.map((product) => (
                  <ProductCard key={product.id} product={product} compact />
                ))}
              </div>
            )}
          </main>
        </div>
      </div>

      {/* Mobile Filter Drawer */}
      {mobileFiltersOpen && (
        <>
          <div
            className="fixed inset-0 bg-black/50 z-40 lg:hidden"
            onClick={() => setMobileFiltersOpen(false)}
          />
          <div className="fixed inset-y-0 left-0 w-80 max-w-full bg-white z-50 lg:hidden overflow-y-auto">
            <div className="p-4 border-b border-gray-200 flex items-center justify-between sticky top-0 bg-white z-10">
              <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2 font-poppins">
                <SlidersHorizontal className="w-5 h-5" />
                Filter Options
                {activeFilterCount > 0 && (
                  <span className="ml-1 px-2 py-0.5 bg-primary-600 text-white text-xs font-semibold rounded-full">
                    {activeFilterCount}
                  </span>
                )}
              </h2>
              <button
                onClick={() => setMobileFiltersOpen(false)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                aria-label="Close filters"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4 space-y-6">
              {/* Quick Filter Presets */}
              <div>
                <h3 className="text-sm font-semibold text-gray-900 mb-3 uppercase tracking-wide font-poppins">
                  Quick Filters
                </h3>
                <div className="space-y-2">
                  <button
                    onClick={() => handleQuickPreset("under_1000")}
                    className="w-full text-left px-3 py-2 text-sm rounded-lg border border-gray-200 hover:border-primary-300 hover:bg-primary-50 transition-colors font-poppins"
                  >
                    Under Rs. 1,000
                  </button>
                  <button
                    onClick={() => handleQuickPreset("1000_5000")}
                    className="w-full text-left px-3 py-2 text-sm rounded-lg border border-gray-200 hover:border-primary-300 hover:bg-primary-50 transition-colors font-poppins"
                  >
                    Rs. 1,000 - Rs. 5,000
                  </button>
                  <button
                    onClick={() => handleQuickPreset("5000_10000")}
                    className="w-full text-left px-3 py-2 text-sm rounded-lg border border-gray-200 hover:border-primary-300 hover:bg-primary-50 transition-colors font-poppins"
                  >
                    Rs. 5,000 - Rs. 10,000
                  </button>
                  <button
                    onClick={() => handleQuickPreset("above_10000")}
                    className="w-full text-left px-3 py-2 text-sm rounded-lg border border-gray-200 hover:border-primary-300 hover:bg-primary-50 transition-colors font-poppins"
                  >
                    Above Rs. 10,000
                  </button>
                </div>
              </div>

              {/* Price Filter */}
              <div>
                <h3 className="text-sm font-semibold text-gray-900 mb-4 uppercase tracking-wide font-poppins">
                  Price Range
                </h3>
                <PriceRangeSlider
                  min={priceStats.min}
                  max={priceStats.max}
                  value={priceRange}
                  onChange={setPriceRange}
                />
              </div>

              {/* Category Filter */}
              <div>
                <h3 className="text-sm font-semibold text-gray-900 mb-3 uppercase tracking-wide font-poppins">
                  By Categories
                </h3>
                <div className="space-y-2">
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleAllCategoriesClick();
                    }}
                    type="button"
                    className={`w-full text-left px-4 py-2.5 text-sm rounded-lg border-2 transition-all font-poppins ${
                      !selectedCategoryId && !selectedPrimaryCategoryName
                        ? "bg-primary-600 text-white border-primary-600 font-medium"
                        : "bg-white text-gray-700 border-gray-200 hover:border-primary-300 hover:bg-primary-50"
                    }`}
                  >
                    All Categories
                  </button>

                  {/* Primary Category Buttons */}
                  {categories.map((category: Category) => {
                    const isActive = selectedParentCategoryId === category.id || selectedPrimaryCategoryName === category.name;
                    return (
                      <div key={category.id}>
                        <CategoryButton
                          category={category}
                          isActive={isActive}
                          onClick={() => handleCategoryClick(category)}
                        />
                        {/* Subcategories - shown directly below selected category */}
                        {childCategories && childCategories.length > 0 && selectedParentCategoryId === category.id && (
                          <div className="mt-1 ml-3 space-y-1 border-l-2 border-primary-200 pl-2">
                            {childCategories.map((child: Category) => {
                              const isSubActive = selectedCategoryId === child.id;
                              return (
                                <SubcategoryButton
                                  key={child.id}
                                  subcategory={child}
                                  isActive={isSubActive}
                                  onClick={() => handleSubcategoryClick(child, category.id)}
                                />
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Sizes Filter */}
              {availableSizes.length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-gray-900 mb-3 uppercase tracking-wide font-poppins">
                    Sizes
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {availableSizes.map((size) => (
                      <button
                        key={size}
                        onClick={() => handleSizeToggle(size)}
                        className={`px-3 py-1.5 text-sm rounded-lg border transition-all font-poppins ${
                          selectedSizes.includes(size)
                            ? "bg-primary-600 text-white border-primary-600 font-medium"
                            : "bg-white text-gray-700 border-gray-200 hover:border-primary-300 hover:bg-primary-50"
                        }`}
                      >
                        {size}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Colors Filter */}
              {availableColors.length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-gray-900 mb-3 uppercase tracking-wide font-poppins">
                    Colors
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {availableColors.map((color) => (
                      <button
                        key={color}
                        onClick={() => handleColorToggle(color)}
                        className={`px-3 py-1.5 text-sm rounded-lg border transition-all font-poppins ${
                          selectedColors.includes(color)
                            ? "bg-primary-600 text-white border-primary-600 font-medium"
                            : "bg-white text-gray-700 border-gray-200 hover:border-primary-300 hover:bg-primary-50"
                        }`}
                      >
                        {color}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Clear All Filters Button */}
              {activeFilterCount > 0 && (
                <div className="pt-4 border-t border-gray-200">
                  <button
                    onClick={handleClearAllFilters}
                    className="w-full py-2.5 px-4 bg-primary-600 text-white text-sm font-medium rounded-lg hover:bg-primary-700 transition-colors font-poppins"
                  >
                    Clear All Filters
                  </button>
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default CategoriesPageNew;
